import { useEffect } from 'react';
import { useModal } from '@/hooks/useModal';
import { BlockedTimeDetailsModal } from './BlockedTimeDetailsModal';
import { BlockTimeModal } from './BlockTimeModal';
import { DeleteBlockedTimeModal } from './DeleteBlockedTimeModal';
import type { Appointment } from '../types/appointment.types';
import { parseLocalDate } from '@/lib/utils/timeUtils';

interface BlockedTimeModalsProps {
    selectedBlockedTime: Appointment | null;
    onClose: () => void;
    onSaveBlockTime: (blockTime: {
        id?: string;
        startDate: string;
        startTime: string;
        endDate: string;
        endTime: string;
    }) => Promise<void>;
    onDeleteBlockTime: (blockedTimeId: string) => Promise<void>;
    isDeleting?: boolean;
}

export function BlockedTimeModals({
    selectedBlockedTime,
    onClose,
    onSaveBlockTime,
    onDeleteBlockTime,
    isDeleting = false,
}: BlockedTimeModalsProps) {
    const detailsModal = useModal();
    const editModal = useModal();
    const deleteModal = useModal();

    // Open details modal when selectedBlockedTime is set
    useEffect(() => {
        if (selectedBlockedTime) {
            detailsModal.open();
        }
    }, [selectedBlockedTime, detailsModal]);

    if (!selectedBlockedTime) return null;

    const handleEdit = () => {
        detailsModal.close();
        editModal.open();
    };

    const handleDelete = () => {
        detailsModal.close();
        deleteModal.open();
    };

    const handleConfirmDelete = async () => {
        if (selectedBlockedTime) {
            await onDeleteBlockTime(selectedBlockedTime.id);
            deleteModal.close();
            onClose();
        }
    };

    const handleSaveEdit = async (blockTime: {
        id?: string;
        startDate: string;
        startTime: string;
        endDate: string;
        endTime: string;
    }) => {
        await onSaveBlockTime(blockTime);
        editModal.close();
        onClose();
    };

    const handleCloseDetails = () => {
        detailsModal.close();
        onClose();
    };

    return (
        <>
            <BlockedTimeDetailsModal
                isOpen={detailsModal.isOpen}
                onClose={handleCloseDetails}
                blockedTime={{
                    id: selectedBlockedTime.id,
                    startDate: selectedBlockedTime.date,
                    endDate: selectedBlockedTime.date,
                    startTime: selectedBlockedTime.startTime,
                    endTime: selectedBlockedTime.endTime,
                }}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />

            <BlockTimeModal
                isOpen={editModal.isOpen}
                onClose={editModal.close}
                selectedDate={parseLocalDate(selectedBlockedTime.date)}
                onSave={handleSaveEdit}
                blockedTimeId={selectedBlockedTime.id}
                initialStartTime={selectedBlockedTime.startTime}
                initialEndTime={selectedBlockedTime.endTime}
            />

            <DeleteBlockedTimeModal
                isOpen={deleteModal.isOpen}
                onClose={deleteModal.close}
                blockedTime={{
                    startDate: selectedBlockedTime.date,
                    endDate: selectedBlockedTime.date,
                    startTime: selectedBlockedTime.startTime,
                    endTime: selectedBlockedTime.endTime,
                }}
                onConfirm={handleConfirmDelete}
                isDeleting={isDeleting}
            />
        </>
    );
}
